import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Loader2 } from 'lucide-react';
import { documentsAnalysisApi } from '../lib/api';
import { getPrivateKey } from '../lib/crypto';
import { toast } from 'sonner';

/**
 * Explains why clicking an already-"Analysed" document did nothing, instead
 * of silently re-running the same pipeline on the same bytes. Offers to
 * analyse again only when a newer prompt version exists since this document
 * was last analysed (`stale`, from GET /documents/{id}/analysis-log) —
 * otherwise the only way forward is a newer copy of the file.
 */
export const AlreadyAnalysedDialog = ({ doc, open, onOpenChange, onReanalysed, onUploadNewer }) => {
  const [loading, setLoading] = useState(true);
  const [stale, setStale] = useState(false);
  const [reanalysing, setReanalysing] = useState(false);

  useEffect(() => {
    if (!open || !doc?.id) return;
    setLoading(true);
    setStale(false);
    documentsAnalysisApi.log(doc.id)
      .then((res) => setStale(!!res.data?.stale))
      .catch(() => setStale(false))
      .finally(() => setLoading(false));
  }, [open, doc?.id]);

  const handleReanalyse = async () => {
    const privateKey = getPrivateKey();
    if (!privateKey) {
      toast.error('Your encryption key is not unlocked. Sign in again.');
      return;
    }
    setReanalysing(true);
    try {
      await documentsAnalysisApi.analyse(doc.id, privateKey);
      toast.success('Queued for analysis on the latest version.');
      onOpenChange(false);
      onReanalysed?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'That could not be queued. Try again.');
    } finally {
      setReanalysing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="already-analysed-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-left text-[20px] font-semibold tracking-[-0.02em]">
            Already analysed
          </DialogTitle>
          <DialogDescription className="text-left text-ink-55">
            {doc?.original_filename}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <p className="text-sm text-ink-55 flex items-center gap-2">
            <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" /> Checking for a newer version of our analysis…
          </p>
        ) : stale ? (
          <p className="text-sm text-ink-70">
            We've improved how documents are analysed since this one was last done. You can run it again on the latest version.
          </p>
        ) : (
          <p className="text-sm text-ink-70">
            Running this again would find the same thing — nothing has changed since it was last analysed.
            If you have a newer or clearer copy of this document, upload it and we'll analyse that instead.
          </p>
        )}

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="ghost"
            onClick={() => { onOpenChange(false); onUploadNewer?.(doc); }}
            data-testid="already-analysed-upload-newer"
          >
            Upload a newer copy
          </Button>
          {stale ? (
            <Button onClick={handleReanalyse} disabled={reanalysing} data-testid="already-analysed-reanalyse">
              {reanalysing && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" aria-hidden="true" />}
              Analyse on the latest version
            </Button>
          ) : (
            <Button onClick={() => onOpenChange(false)} disabled={loading} data-testid="already-analysed-close">
              Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AlreadyAnalysedDialog;
